// src/components/ui/HelpMenuPanel.js
// Right slide-over drawer (D-02) — persistent Help surface.
// Renders via ReactDOM.createPortal to document.body.
// Does NOT extend or wrap the Modal primitive (Modal is centered; this is side-anchored).
// Uses the shared useDialogShell hook for Escape-to-close, body-scroll-lock,
// focus-on-open + focus-return, and Tab focus trap (same behaviors Modal uses).
//
// z-index hierarchy:
//   ToastStack      z-[10000] (top layer: feedback is never hidden by a dialog)
//   Modal           z-[9990]  (centered dialogs)
//   HelpMenuPanel   z-[9985]  (this drawer)
//   HelpFab         z-[9980]  (bottom-right speed-dial FAB, replaces FloatingHelpButton)
//   NudgePopover    z-[9975]  (tour nudge, anchored above the FAB)
//
// JIT TRAP: all dynamic dimension values go via inline style, never template-literal Tailwind classes.
//
// Per-row state (D-03/D-04, plan 42-06): each Guides-list row renders its
// own New or Taken badge, a muted "Tips off" marker when its nudge was
// dismissed (a dismissed tour still reads New, never Taken), and an inline
// reason when the tour cannot run here (D-12, plan 42-04). There is no
// per-row un-suppress control — "Take the tour" is always available, so a
// dismissed tour's only way back is taking it.
//
// Report a bug lives in a pinned footer below the scrollable body (D-09),
// not as the last item inside it — see the footer block in the component
// below for why.

import React from 'react';
import ReactDOM from 'react-dom';
import * as Sentry from '@sentry/react';
import IconButton from './primitives/IconButton';
import Badge from './primitives/Badge';
import useDialogShell from './primitives/useDialogShell';
import { useTour } from '../features/tours/TourContext';
import GuideViewer from './GuideViewer';
import HelpSearch from '../features/help/HelpSearch';
import catalog from '../../guideCatalog.json';
import { HELP_PANEL_LIMIT } from '../../utils/announcements';
import WhatsNewRow from '../features/whatsNew/WhatsNewRow';
import Button from './primitives/Button';

// All tourIds the drawer's Guides list renders, derived from the catalog
// (not the registry) so this list needs no dynamic import of its own — the
// registry is only ever reached dynamically, on drawer open (D-13).
const CATALOG_TOUR_IDS = catalog.groups.flatMap((group) => group.tours.map((tour) => tour.tourId));

// ---- Icons ----

function CloseIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}

function ChevronRightIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <polyline points="9 18 15 12 9 6" />
        </svg>
    );
}

function RefreshIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
    );
}

function BookIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
    );
}

function BugIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <path d="M8 2l1.88 1.88" />
            <path d="M14.12 3.88 16 2" />
            <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
            <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6z" />
            <path d="M12 20v-9" />
            <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
            <path d="M6 13H2" />
            <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
            <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
            <path d="M22 13h-4" />
            <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
        </svg>
    );
}

// ---- Static Guides list ----
// guideCatalog.json is the single source of truth for groups and tours (D-03).
// The hardcoded constant was removed; catalog.groups drives rendering directly.

// ---- Menu view ----

// Section eyebrow treatment (D-09) — identical to the former standalone
// "Guides" label this replaces: 12px/600/uppercase/0.06em/text-muted.
const SECTION_EYEBROW_STYLE = {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.06em',
    color: 'var(--text-muted)',
    marginBottom: 8,
};

// The one reserved gold use for this section (UI-SPEC): a small dot on the
// eyebrow when an unseen announcement exists. aria-hidden because the sr-only
// sibling text carries the same information for assistive tech.
function UnseenDot() {
    return (
        <>
            <span
                aria-hidden="true"
                className="inline-block rounded-full"
                style={{ width: 6, height: 6, background: 'var(--color-accent)' }}
            />
            <span className="sr-only">New updates</span>
        </>
    );
}

// D-26: the panel shows only the newest few; the full history lives on the
// signed-in /whats-new page, reached by the "See all updates" link.
function WhatsNewSection({ whatsNew, headingRef = null, onSeeAllUpdates = null }) {
    const { helpList: fullList = [], helpPanel = null, hasUnseen = false, error = null, loading = false } = whatsNew || {};
    const helpList = helpPanel?.items ?? fullList.slice(0, HELP_PANEL_LIMIT);
    const headingId = React.useId();

    return (
        <div data-tour="whats-new-list" style={{ marginBottom: 24 }}>
            {/* tabIndex -1 + ref: the sidebar's build-version link opens the
                drawer straight onto this heading (D-27). */}
            <h2
                ref={headingRef}
                id={headingId}
                tabIndex={-1}
                className="uppercase flex items-center gap-2 focus:outline-none"
                style={SECTION_EYEBROW_STYLE}
            >
                What's New
                {hasUnseen && <UnseenDot />}
            </h2>
            {error ? (
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    Update history didn't load. Refresh the page to try again.
                </p>
            ) : helpList.length === 0 && loading ? (
                <p role="status" className="text-sm" style={{ color: 'var(--text-muted)', padding: '16px 0' }}>
                    Loading updates
                </p>
            ) : helpList.length === 0 ? (
                <div className="text-center" style={{ padding: '16px 0' }}>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>No updates yet</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                        New releases will show up here as they ship.
                    </p>
                </div>
            ) : (
                <div>
                    {helpList.map((announcement, i) => (
                        <WhatsNewRow
                            key={announcement.id}
                            announcement={announcement}
                            isLast={i === helpList.length - 1}
                        />
                    ))}
                </div>
            )}
            {fullList.length > 0 && !error && (
                <Button
                    variant="link"
                    className="text-sm"
                    style={{ marginTop: 8 }}
                    onClick={onSeeAllUpdates || undefined}
                >
                    See all updates
                </Button>
            )}
        </div>
    );
}

function MenuView({ onRestartOnboarding, hasWorkspace = true, launchTour, onReadGuide, availability, launchError, hasTakenTour, isNudgeSuppressed, whatsNew, whatsNewHeadingRef, onSeeAllUpdates }) {
    return (
        <div className="flex-1 overflow-y-auto" style={{ padding: '16px 20px' }}>
            {/* First control-center section (D-27, moved from last by UAT gap 2):
                the permanent What's New list leads the menu so it is easy to find. */}
            <WhatsNewSection whatsNew={whatsNew} headingRef={whatsNewHeadingRef} onSeeAllUpdates={onSeeAllUpdates} />

            {/* Second control-center section (D-09): help-seeking entry points. */}
            <p className="uppercase" style={SECTION_EYEBROW_STYLE}>Get help</p>

            {/* Browse all help articles row (D-13): every in-app link to an
                article opens a new tab — someone reaching for help is
                mid-transaction, mid-composer, or mid-tour, and navigating
                away risks losing that work, so this rule has no exceptions.
                This reverses Phase 44's D-04, which navigated in-app via an
                optional caller-supplied navigation callback; that callback
                prop is gone entirely.
                A plain anchor, not a raw button element (the raw-element
                ratchet already baselines this file's button count) and not
                a react-router Link (this component is mounted with no
                router in tests/HelpMenuPanel.test.js). */}
            <a
                href="/help"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="help-browse-articles"
                className="w-full flex items-center gap-3 rounded-[10px] text-left
                           hover:bg-[color:var(--surface-hover)]
                           focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]/50"
                style={{ background: 'var(--surface-muted)', padding: '10px 14px', marginBottom: 24 }}
            >
                <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}><BookIcon /></span>
                <span className="flex-1 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Browse all help articles</span>
                <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}><ChevronRightIcon /></span>
            </a>

            {/* Third control-center section (D-09) — replaces the former
                standalone eyebrow this file used to render above the Guides
                list alone; the tour-launching rows now group under it. */}
            <p className="uppercase" style={SECTION_EYEBROW_STYLE}>Take a tour</p>

            {/* Restart Onboarding row (SA-01). Disabled when there is no selected
                workspace (bug: 2026-09-24-onboarding-wizard-before-plan-choice.md) --
                the wizard's steps are workspace-scoped, so replaying it with nowhere
                to write would look like it worked and silently lose the input. Same
                disabled-row treatment as the tour rows below (aria-disabled + a hint
                describing why), rather than a dead button with no explanation. */}
            <button
                type="button"
                data-testid="help-restart-onboarding"
                onClick={onRestartOnboarding}
                disabled={!hasWorkspace}
                aria-disabled={!hasWorkspace}
                aria-describedby={!hasWorkspace ? 'help-restart-onboarding-hint' : undefined}
                className="w-full flex items-center gap-3 rounded-[10px] text-left
                           hover:bg-[color:var(--surface-hover)]
                           focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]/50
                           disabled:hover:bg-transparent"
                style={{
                    background: 'var(--surface-muted)',
                    padding: '10px 14px',
                    marginBottom: hasWorkspace ? 24 : 4,
                    opacity: hasWorkspace ? 1 : 0.6,
                    cursor: hasWorkspace ? 'pointer' : 'default',
                }}
            >
                <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}><RefreshIcon /></span>
                <span className="flex-1 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Replay setup</span>
                <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}><ChevronRightIcon /></span>
            </button>
            {!hasWorkspace && (
                <p
                    id="help-restart-onboarding-hint"
                    className="text-xs"
                    style={{ color: 'var(--text-muted)', marginBottom: 20 }}
                >
                    Available once you have a workspace.
                </p>
            )}

            <div data-tour="guides-list" style={{ marginBottom: 24 }}>
                {catalog.groups.map((group, gi) => (
                    <div key={group.label} style={{ marginBottom: gi < catalog.groups.length - 1 ? 20 : 0 }}>
                        {/* Group sub-label */}
                        <p
                            className="uppercase"
                            style={{
                                fontSize: 12,
                                fontWeight: 600,
                                letterSpacing: '0.06em',
                                color: 'var(--text-muted)',
                                marginBottom: 4,
                            }}
                        >
                            {group.label}
                        </p>
                        {group.tours.map((tour, ti) => {
                            // Absent entry = available: the recompute effect hasn't
                            // resolved yet (or this tourId isn't in the registry at
                            // all), and a false-positive "available" row that then
                            // hits the inline typed-failure path is honest — the
                            // alternative is flashing every row disabled on open.
                            const info = availability[tour.tourId];
                            const isUnavailable = Boolean(info && !info.available);
                            const hintId = `help-menu-hint-${tour.tourId}`;
                            const rowLaunchError = !isUnavailable && launchError?.tourId === tour.tourId
                                ? launchError.hint
                                : null;

                            // The badge is decided by hasTakenTour ALONE, never by
                            // isNudgeSuppressed (D-07 call site) — using suppression
                            // here would relabel a dismissed-but-unseen tour as
                            // Taken, the exact conflation D-04 forbids.
                            const taken = hasTakenTour(tour.tourId);
                            // D-04: a dismissed tour still reads as untaken. This
                            // composition is exact for precisely the 'dismissed'
                            // case: isNudgeSuppressed is true for
                            // completed | skipped | dismissed (D-08's closed status
                            // set), and subtracting the taken statuses
                            // (completed | skipped) leaves only 'dismissed'.
                            const dismissed = isNudgeSuppressed(tour.tourId) && !hasTakenTour(tour.tourId);

                            return (
                                <div
                                    key={tour.tourId}
                                    data-tour-row={tour.tourId}
                                    style={{
                                        padding: '10px 0',
                                        borderBottom: ti < group.tours.length - 1
                                            ? '1px solid var(--border-color)'
                                            : 'none',
                                    }}
                                >
                                    {/* Name line and actions share one row; the hint and the
                                        launch error sit BELOW it so they can use the drawer's
                                        full width instead of the narrow column left over
                                        beside the action links. */}
                                    <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0" style={{ paddingRight: 12 }}>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span
                                                className="text-sm font-semibold"
                                                style={{ color: isUnavailable ? 'var(--text-muted)' : 'var(--text-primary)' }}
                                            >
                                                {tour.name}
                                            </span>
                                            <Badge tone={taken ? 'success' : 'neutral'} size="sm">
                                                {taken ? 'Taken' : 'New'}
                                            </Badge>
                                            {dismissed && (
                                                <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                                                    Tips off
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3" style={{ flexShrink: 0 }}>
                                        <button
                                            type="button"
                                            onClick={() => launchTour(tour.tourId)}
                                            disabled={isUnavailable}
                                            aria-disabled={isUnavailable}
                                            aria-describedby={isUnavailable ? hintId : undefined}
                                            className="text-sm focus:outline-none focus:underline
                                                       hover:underline disabled:hover:no-underline disabled:focus:no-underline"
                                            style={{
                                                color: isUnavailable ? 'var(--text-muted)' : 'var(--action-link-color)',
                                                cursor: isUnavailable ? 'default' : 'pointer',
                                                opacity: isUnavailable ? 0.6 : 1,
                                            }}
                                        >
                                            Take the tour
                                        </button>
                                        <button
                                            type="button"
                                            aria-label={`Read the guide for ${tour.name}`}
                                            onClick={() => onReadGuide(tour.tourId)}
                                            className="text-sm focus:outline-none focus:underline hover:underline"
                                            style={{ color: 'var(--action-link-color)' }}
                                        >
                                            Read the guide
                                        </button>
                                    </div>
                                    </div>
                                    {isUnavailable && info?.hint && (
                                        <p id={hintId} className="text-xs" style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                                            {info.hint}
                                        </p>
                                    )}
                                    {rowLaunchError && (
                                        <p className="text-xs" style={{ color: 'var(--color-danger)', marginTop: 4 }}>
                                            {rowLaunchError}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ---- Tour availability (shared by the drawer and the embedded help) ----

// availability: { [tourId]: { available: boolean, hint: string|null } }.
// Starts empty; an absent entry is treated as available (see MenuView), so the
// surface never flashes every row disabled before the first recompute resolves.
// launchError: { tourId, hint } | null, the D-14 "everything else" case,
// rendered inline on the offending row and never as a toast.
function useTourAvailability(open) {
    const [availability, setAvailability] = React.useState({});
    const [launchError, setLaunchError] = React.useState(null);

    // Recompute availability every time the surface opens: the DOM can change
    // while it is closed (D-13). The registry is reached ONLY through this
    // dynamic import: this file is in the eager tree, and a static import would
    // drag all 19 step-builder modules into the initial bundle.
    React.useEffect(() => {
        if (!open) return undefined;
        setLaunchError(null);
        let cancelled = false;
        (async () => {
            const { tourRegistry, filterAvailableSteps } = await import('../features/tours/tourRegistry');
            const next = {};
            for (const tourId of CATALOG_TOUR_IDS) {
                const tourDef = tourRegistry[tourId];
                if (!tourDef) continue;
                if (tourDef.route) {
                    // Routed tours stay available: launchTour() navigates first and
                    // surfaces anchor drift through the inline typed-failure path.
                    next[tourId] = { available: true, hint: null };
                    continue;
                }
                // Routeless tours: the same check launchTour() runs, so the two
                // can never disagree (D-13).
                const available = filterAvailableSteps(tourDef.buildSteps()).length > 0;
                next[tourId] = { available, hint: available ? null : (tourDef.requiresContext ?? null) };
            }
            if (!cancelled) setAvailability(next);
        })();
        return () => {
            cancelled = true;
        };
    }, [open]);

    return { availability, launchError, setLaunchError };
}

function hasSentryFeedback() {
    try { return typeof Sentry.getFeedback === 'function' && Sentry.getFeedback() != null; }
    catch { return false; }
}

async function openSentryFeedback() {
    try {
        const feedback = Sentry.getFeedback?.();
        if (!feedback) return;
        const form = await feedback.createForm();
        form.appendToDom();
        form.open();
    } catch {
        // Sentry unavailable: degrade silently.
    }
}

function ReportBugRow({ onClick }) {
    return (
        <button
            type="button"
            data-testid="help-report-bug"
            onClick={onClick}
            className="w-full flex items-center gap-3 rounded-[10px] text-left
                       hover:bg-[color:var(--surface-hover)]
                       focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]/50"
            style={{ background: 'var(--surface-muted)', padding: '10px 14px' }}
        >
            <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}><BugIcon /></span>
            <span className="flex-1 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Report a bug</span>
            <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}><ChevronRightIcon /></span>
        </button>
    );
}

// The help sections (search, What's New, guides, tours, Report a bug) as one
// block, for hosts that are not the Help drawer: the KeyFlow AI dock and the
// drawer's KeyFlow AI mode. The Help drawer's own body is unchanged.
export function HelpSections({ onClose, restartOnboarding, hasWorkspace = true, whatsNew = {}, onSeeAllUpdates, onReadGuide }) {
    const { launchTour, hasTakenTour, isNudgeSuppressed } = useTour();
    const { availability, launchError, setLaunchError } = useTourAvailability(true);
    const [searchActive, setSearchActive] = React.useState(false);
    const [guideId, setGuideId] = React.useState(null);
    const hasSentry = React.useMemo(() => hasSentryFeedback(), []);
    const close = typeof onClose === 'function' ? onClose : () => {};

    const handleLaunchTour = async (tourId) => {
        setLaunchError(null);
        const result = await launchTour(tourId);
        if (result?.ok) { close(); return; }
        setLaunchError({ tourId, hint: result?.hint ?? null });
    };
    const handleRestart = () => {
        if (!hasWorkspace) return;
        close();
        if (typeof restartOnboarding === 'function') restartOnboarding();
    };
    const handleReadGuide = (tourId) => {
        if (typeof onReadGuide === 'function') { onReadGuide(tourId); return; }
        setGuideId(tourId);
    };

    return (
        <>
            <div style={{ padding: '12px 20px 8px' }}>
                <HelpSearch onActiveChange={setSearchActive} />
            </div>
            {!searchActive && (
                <MenuView
                    onRestartOnboarding={handleRestart}
                    hasWorkspace={hasWorkspace}
                    launchTour={handleLaunchTour}
                    onReadGuide={handleReadGuide}
                    availability={availability}
                    launchError={launchError}
                    hasTakenTour={hasTakenTour}
                    isNudgeSuppressed={isNudgeSuppressed}
                    whatsNew={whatsNew}
                    whatsNewHeadingRef={null}
                    onSeeAllUpdates={() => {
                        close();
                        if (typeof onSeeAllUpdates === 'function') onSeeAllUpdates();
                    }}
                />
            )}
            {hasSentry && (
                <div className="flex-shrink-0 border-t border-[color:var(--border-color)]" style={{ padding: '12px 20px' }}>
                    <ReportBugRow onClick={async () => { close(); await openSentryFeedback(); }} />
                </div>
            )}
            {guideId && <GuideViewer tourId={guideId} onClose={() => setGuideId(null)} />}
        </>
    );
}

// ---- HelpMenuPanel ----

export default function HelpMenuPanel({ open, onClose, restartOnboarding, hasWorkspace = true, whatsNew = {}, focusWhatsNew = false, onFocusWhatsNewHandled, onSeeAllUpdates, keyflowAiSlot = null }) {
    const titleId = React.useId();
    const [isEntering, setIsEntering] = React.useState(false);
    const { launchTour, hasTakenTour, isNudgeSuppressed } = useTour();
    const [guideOpen, setGuideOpen] = React.useState(false);
    const [guideId, setGuideId] = React.useState(null);
    // 47-08 (ACCESS-06): the element GuideViewer restores focus to on close —
    // the Help FAB, not the "Read the guide" button that opened it. See
    // handleReadGuide's own comment for why the literal clicked button is
    // not a viable restore target.
    const guideTriggerRef = React.useRef(null);
    // D-10: the ref useDialogShell focuses on open, and D-03: whether an
    // active search query is replacing the drawer body below the field.
    const searchInputRef = React.useRef(null);
    const [searchActive, setSearchActive] = React.useState(false);
    // D-27: the What's New heading, focused when the sidebar build-version
    // link opens the drawer (focusWhatsNew).
    const whatsNewHeadingRef = React.useRef(null);

    // Sentry getFeedback availability — undefined when Sentry is not
    // initialised (dev / no DSN). Moved from HelpFab.js (D-09): the row it
    // gates now lives in this drawer's third control-center section below.
    const hasSentry = React.useMemo(() => hasSentryFeedback(), []);

    // Tour availability and the inline launch error live in useTourAvailability
    // so the embedded help (KeyFlow AI dock) shares them.
    const { availability, launchError, setLaunchError } = useTourAvailability(open);

    // Trigger slide-in on next frame so CSS transition fires
    React.useEffect(() => {
        if (open) {
            requestAnimationFrame(() => setIsEntering(true));
        } else {
            setIsEntering(false);
        }
    }, [open]);

    // D-10 correction (47-p2-sweep): HelpMenuPanel does NOT mount/unmount per
    // `open` the way Modal does — App.js renders it unconditionally and only
    // ever toggles the `open` prop (the useDialogShell call below's own
    // comment claims otherwise; that claim is stale and wrong). useDialogShell's
    // focus-on-open effect has deps: [] BY DESIGN, matching Modal's real
    // mount/unmount contract, so it only ever fires once — at HelpMenuPanel's
    // one true mount, at app boot, while `open` is still false. Without this
    // second effect, keyed on `open` itself, opening the drawer later never
    // moves focus anywhere: a keyboard user who opens it via the FAB or the
    // `?` shortcut lands nowhere, which is exactly the gap ACCESS-01 and
    // ACCESS-06 exist to close. Caught live by e2e/helpAccessibility.spec.js,
    // which found focus stranded on the FAB's own button after open.
    //
    // D-27: when opened via the build-version link (focusWhatsNew), focus the
    // What's New heading instead, once, then report it handled so later opens
    // return to the search field. HelpSearch has no external clear path, but
    // it unmounts with the drawer and searchActive resets on close, so a
    // fresh open always renders MenuView; if a query is somehow active (the
    // heading is not mounted), fall back to the search field.
    //
    // The search field is focused ONLY on the closed-to-open transition
    // (wasOpenRef). The handled callback makes the parent flip focusWhatsNew
    // back to false, which re-runs this effect; without the transition guard
    // that re-run would take the search branch and pull focus off the heading
    // the user was just sent to (B-CR-02).
    const wasOpenRef = React.useRef(false);
    React.useEffect(() => {
        const justOpened = open && !wasOpenRef.current;
        wasOpenRef.current = open;
        if (!open) return undefined;
        if (!focusWhatsNew) {
            if (justOpened) searchInputRef.current?.focus();
            return undefined;
        }
        const frame = requestAnimationFrame(() => {
            const heading = whatsNewHeadingRef.current;
            if (heading) heading.focus();
            else searchInputRef.current?.focus();
            if (typeof onFocusWhatsNewHandled === 'function') onFocusWhatsNewHandled();
        });
        return () => cancelAnimationFrame(frame);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on open/focusWhatsNew only; the handled callback is an unstable inline arrow
    }, [open, focusWhatsNew]);

    // Reset searchActive whenever the drawer closes, so reopening never lands
    // on a stale result list. HelpSearch itself unmounts with the drawer
    // (its own query state resets); only this parent flag needs the
    // explicit reset, since it survives across HelpSearch's mount/unmount.
    React.useEffect(() => {
        if (!open) setSearchActive(false);
    }, [open]);

    // Shared dialog-shell behaviors: Escape-to-close, scroll-lock, and Tab
    // focus trap all stay correct even though HelpMenuPanel is mounted for
    // the app's whole lifetime rather than per-open (see the correction
    // above): closeOnEscape and trapFocus read `open` state or containerRef
    // freshly through their own live refs/DOM queries on every keypress, so
    // they behave correctly whether or not the drawer is currently visible.
    // useDialogShell's OWN focus-on-open/focus-return effects are mount-once
    // by design (matching Modal's real mount/unmount contract) and do NOT
    // re-fire on this component's `open` toggling — the effect above this
    // hook call exists specifically to cover that gap for focus-on-open.
    const { containerRef } = useDialogShell({
        onClose,
        closeOnEscape: true,
        // D-10: focus lands in the search field on open, not the dialog
        // container — useDialogShell already supports an alternate focus
        // target via the option set below. Only takes effect at
        // HelpMenuPanel's own mount, per the correction above; the repeat-open
        // case is covered by the dedicated effect right before this hook call.
        initialFocusRef: searchInputRef,
        trapFocus: true,
        lockScroll: true,
    });

    // NOTE: do NOT early-return on `!open`. "Read the guide" closes the drawer
    // (onClose → open=false) and opens the GuideViewer in the same handler; an early
    // return here would unmount the viewer subtree before it could paint. Instead the
    // drawer portal is gated on `open` below while the viewer renders on `guideOpen`.

    // Bug: 2026-09-24-onboarding-wizard-before-plan-choice.md. The wizard's steps
    // are workspace-scoped, so replaying it with no workspace selected (e.g. a
    // brand-new account still on plan selection) would open a wizard whose writes
    // silently go nowhere. The row itself is disabled below when !hasWorkspace;
    // this guard is the same rule applied at the handler, in case the row is ever
    // reached another way.
    const handleRestartOnboarding = () => {
        if (!hasWorkspace) return;
        onClose();
        if (typeof restartOnboarding === 'function') restartOnboarding();
    };

    // Close the drawer before driver.js paints its overlay, so the backdrop
    // and focus trap are gone by the time the tour begins (Finding C).
    // Closing now happens in the very next synchronous continuation after
    // `launchTour()` resolves, with no intervening macrotask on the ok path
    // (any route-navigation wait inside launchTour completes strictly
    // BEFORE drive() is ever called), so the browser never gets a chance to
    // paint an intermediate "overlay + open drawer" frame.
    //
    // On a non-ok result driver.js never calls drive() at all — every
    // failure branch inside launchTour returns before a driver instance is
    // even created — so there is no overlay to race against. The drawer
    // stays open and renders the failure inline instead: this is D-14's
    // "everything else" case (an enabled row, already past the availability
    // check above, that still failed to launch — an unknown tourId, or
    // anchor drift discovered on a routed tour after navigation). Per D-15/
    // Pitfall 5's per-call-site convention this is rendered inline, never a
    // toast — the nudge and direct page CTAs own the toast-rendering path.
    const handleLaunchTour = async (tourId) => {
        setLaunchError(null);
        const result = await launchTour(tourId);
        if (result?.ok) {
            onClose();
            return;
        }
        setLaunchError({ tourId, hint: result?.hint ?? null });
    };

    // Close the drawer before opening the lightbox — no double-overlay stacking.
    //
    // 47-08 (ACCESS-06): onClose() and setGuideOpen(true) below both fire in
    // this one synchronous handler, so React batches them into a single
    // commit that unmounts the drawer's portal — the clicked "Read the
    // guide" button included — and mounts GuideViewer's Modal in the same
    // pass. Capturing document.activeElement here would capture that
    // button, but restoring focus to it later (when the guide closes) is a
    // silent no-op: calling .focus() on a node the DOM has already detached
    // does nothing, per the focus spec, so the clicked button is not a
    // reachable restore target once this handler returns. The Help FAB is
    // the one anchor across this entire drawer -> guide chain that is never
    // unmounted (App.js always renders it), so it is the restore target
    // that is actually achievable: closing the guide returns focus to the
    // control that is still there to receive it. Verified live:
    // e2e/helpAccessibility.spec.js found the literal clicked-button target
    // silently failing before this comment existed.
    const handleReadGuide = (tourId) => {
        guideTriggerRef.current = document.querySelector('[data-tour="help-button"]');
        onClose();
        setGuideId(tourId);
        setGuideOpen(true);
    };

    // Report-a-bug (D-09) — moved from HelpFab.js's handleReportBug, which
    // this file's own handleReadGuide/handleLaunchTour pattern (and
    // HelpFab's now-removed handleOpenHelp) already established: close this
    // surface before opening the next one, so there is never a double
    // overlay. The counter-argument was raised directly and answered the
    // same way — you report a bug about the page you are looking at, which
    // is exactly why the drawer closes before the handoff, not a reason to
    // keep it open.
    const handleReportBug = React.useCallback(async () => {
        onClose();
        await openSentryFeedback();
    }, [onClose]);

    // KeyFlow AI mode (D-01): the chat replaces the Help header and body; the
    // help sections stay one tap away through the chat's Help button.
    const slotNode = typeof keyflowAiSlot === 'function'
        ? keyflowAiSlot({
            renderHelp: () => (
                <HelpSections
                    onClose={onClose}
                    restartOnboarding={restartOnboarding}
                    hasWorkspace={hasWorkspace}
                    whatsNew={whatsNew}
                    onSeeAllUpdates={onSeeAllUpdates}
                    onReadGuide={handleReadGuide}
                />
            ),
        })
        : keyflowAiSlot;

    const drawerContent = (
        <div
            className="fixed inset-0 z-[9985] bg-black/40 backdrop-blur-[2px]"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                ref={containerRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={slotNode ? undefined : titleId}
                aria-label={slotNode ? 'KeyFlow AI' : undefined}
                tabIndex={-1}
                className="fixed inset-y-0 right-0 flex flex-col w-[360px] max-w-full
                           bg-[color:var(--surface-primary)] border-l border-[color:var(--border-color)]
                           focus:outline-none"
                style={{
                    ...(slotNode ? { width: 'min(420px, 100%)' } : null),
                    boxShadow: '-8px 0 32px rgba(0,0,0,0.18)',
                    transform: isEntering ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 220ms ease-out',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {slotNode ? slotNode : (
                <>
                {/* Header — 56px */}
                <div
                    className="flex flex-shrink-0 items-center justify-between border-b border-[color:var(--border-color)]"
                    style={{ height: 56, paddingLeft: 20, paddingRight: 12 }}
                >
                    <h2
                        id={titleId}
                        className="font-semibold"
                        style={{ fontSize: 20, color: 'var(--text-primary)' }}
                    >
                        Help
                    </h2>
                    <IconButton aria-label="Close Help" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </div>

                {/* Search field (D-02/D-10) — directly beneath the 56px header,
                    above the scrollable body. MiniSearch's dynamic import and the
                    index fetch fire on this field's own focus event (D-04),
                    inside HelpSearch itself: the same discipline the recompute
                    effect above already uses for tourRegistry, via
                    await import(...) rather than a top-level import. That
                    discipline matters more here than anywhere else in this file,
                    because HelpMenuPanel is eagerly imported into App.js — a
                    static import anywhere on this file's path would be the
                    GATE-03 failure mode. */}
                <div style={{ padding: '12px 20px 8px' }}>
                    <HelpSearch inputRef={searchInputRef} onActiveChange={setSearchActive} />
                </div>

                {/* Body — an active search query REPLACES this entirely (D-03);
                    it is never dimmed, animated, or hidden, so clearing the
                    field restores it instantly with no transition delay. */}
                {!searchActive && (
                    <MenuView
                        onRestartOnboarding={handleRestartOnboarding}
                        hasWorkspace={hasWorkspace}
                        launchTour={handleLaunchTour}
                        onReadGuide={handleReadGuide}
                        availability={availability}
                        launchError={launchError}
                        hasTakenTour={hasTakenTour}
                        isNudgeSuppressed={isNudgeSuppressed}
                        whatsNew={whatsNew}
                        whatsNewHeadingRef={whatsNewHeadingRef}
                        onSeeAllUpdates={() => {
                            onClose();
                            if (typeof onSeeAllUpdates === 'function') onSeeAllUpdates();
                        }}
                    />
                )}

                {/* Footer — pinned outside the scrollable body, so "Report a
                    bug" is reachable at every scroll position instead of
                    sitting below a long tour list (D-09 relocation). It
                    persists through an active search query on purpose: it is
                    drawer chrome, the same category as the header, not
                    searchable content — hiding it mid-search would be
                    inconsistent with the header staying put too. Renders
                    nothing at all when Sentry is unavailable (dev / no DSN),
                    never an empty bar. */}
                {hasSentry && (
                    <div
                        className="flex-shrink-0 border-t border-[color:var(--border-color)]"
                        style={{ padding: '12px 20px' }}
                    >
                        <ReportBugRow onClick={handleReportBug} />
                    </div>
                )}
                </>
                )}
            </div>
        </div>
    );

    return (
        <>
            {open && ReactDOM.createPortal(drawerContent, document.body)}
            {guideOpen && (
                <GuideViewer
                    tourId={guideId}
                    onClose={() => setGuideOpen(false)}
                    restoreFocusRef={guideTriggerRef}
                />
            )}
        </>
    );
}
